/**
 * Site Settings V2 — "Features" section (staff-only): the toggleable
 * site capability list, shared with the customer/site-group feature
 * lists and the legacy EditSiteDialog Features tab.
 *
 * Contains:
 * - FeaturesSection (default export)
 */

import TuneIcon from '@mui/icons-material/Tune';
import { Box, List as MuiList, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import StatusChip from '@sophocles/components/StatusChip';
import { loadFeatures, loadSiteFeatureAssociations } from '@sophocles/features/featureFlags/api';
import FeatureCard from '@sophocles/features/featureFlags/components/FeatureCard';
import { selectSortedFeaturesForEntity } from '@sophocles/features/featureFlags/slice';
import { SettingsSection, SettingsSubsection } from '@sophocles/views/settings/components';

// Staff-only Features section.  Shares `<FeatureCard>` with the
// equivalent customer / sitegroup feature lists and the legacy
// EditSiteDialog Features tab — same control, same write path
// (toggles fire createFeatureAssociation / editFeatureAssociation
// immediately, no parent-form save needed).
//
// PlantHOC dispatches loadFeatures when the user lands on the
// plant view, but the V2 settings route can be linked to
// directly so we re-dispatch the loaders here as a self-heal.
// Both thunks no-op when the data is already in the store.
export default function FeaturesSection({ siteId }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const siteFeatures = useSelector(selectSortedFeaturesForEntity('site', siteId));

  React.useEffect(() => {
    dispatch(loadFeatures());
    if (siteId != null) {
      dispatch(loadSiteFeatureAssociations(siteId));
    }
  }, [dispatch, siteId]);

  const staffChip = <StatusChip color="info" label={t('siteSettings.staffOnly', 'Staff Only')} />;
  const isEmpty = !siteFeatures || siteFeatures.length === 0;

  return (
    <SettingsSection
      id="settings-features"
      icon={<TuneIcon />}
      title={t('siteSettings.v2.features', 'Features')}
      description={t(
        'siteSettings.v2.featuresDescription',
        'Toggleable site capabilities. Staff manages which features are available.',
      )}
      chip={staffChip}
    >
      <SettingsSubsection
        title={t('siteSettings.v2.featuresEnabled', 'Available Features')}
        description={t('sites.featuresApplyImmediately', 'Feature changes take effect immediately.')}
        bordered
        flush
      >
        {isEmpty ? (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              {t('siteSettings.v2.noFeatures', 'No site features available.')}
            </Typography>
          </Box>
        ) : (
          <MuiList
            disablePadding
            sx={{
              // Drop the trailing divider on the last FeatureCard so
              // the bordered subsection's edge isn't drawn twice.
              '& > li:last-of-type': { borderBottom: 0 },
            }}
          >
            {siteFeatures.map(feature => (
              <FeatureCard key={feature.key} feature={feature} entityType="site" entityId={siteId} />
            ))}
          </MuiList>
        )}
      </SettingsSubsection>
    </SettingsSection>
  );
}
